import React, { forwardRef } from 'react';
import { cn } from '../../lib/utils';

/**
 * OrkaButton — canonical button (PRD §7.2).
 *
 * Variants:
 *   primary  | violet/dark fill
 *   secondary| outline
 *   ghost    | no background
 *   danger   | red fill
 *   intent   | accepts intent prop for tone
 *
 * Sizes:
 *   sm | md | lg | icon
 *
 * Always includes focus-visible ring + property-specific transitions
 * (no `transition-all` per Rule 7).
 */
const VARIANT_CLASSES = {
  primary: 'bg-violet-600 hover:bg-violet-700 active:bg-violet-800 text-white border border-transparent',
  secondary: 'bg-transparent hover:orka-surface-subtle orka-text-primary border orka-border-default',
  ghost: 'bg-transparent hover:orka-surface-subtle orka-text-secondary border border-transparent',
  danger: 'bg-red-600 hover:bg-red-700 active:bg-red-800 text-white border border-transparent',
  ai: 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white border border-transparent',
};

const SIZE_CLASSES = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-base gap-2.5',
  icon: 'h-9 w-9',
};

export const OrkaButton = forwardRef(function OrkaButton(
  {
    variant = 'secondary',
    size = 'md',
    icon: Icon,
    iconRight: IconRight,
    loading = false,
    fullWidth = false,
    pill = true,
    children,
    className,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isIconOnly = size === 'icon' || (!children && Icon);
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center font-medium select-none',
        'orka-transition-colors orka-focus-ring',
        pill ? 'rounded-full' : 'rounded-lg',
        VARIANT_CLASSES[variant] || VARIANT_CLASSES.secondary,
        SIZE_CLASSES[size] || SIZE_CLASSES.md,
        (disabled || loading) && 'opacity-50 cursor-not-allowed',
        fullWidth && 'w-full',
        className,
      )}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && (
        <svg className="animate-spin shrink-0" width={14} height={14} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
          <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      )}
      {!loading && Icon && <Icon width={size === 'sm' ? 14 : 16} height={size === 'sm' ? 14 : 16} aria-hidden="true" />}
      {children && <span className={cn(isIconOnly && 'sr-only')}>{children}</span>}
      {!loading && IconRight && <IconRight width={size === 'sm' ? 14 : 16} height={size === 'sm' ? 14 : 16} aria-hidden="true" />}
    </button>
  );
});

export default OrkaButton;
